import { useMemo } from 'react';
import {
    DEFAULT_HOUR,
    DEFAULT_MINUTE,
} from '../../widgets/FlightsManager/MobileFields/TimePicker/constants';
import { TimeUtils } from '..';

const toInt = (str) => parseInt(str, 10) || 0;

export const findLastAvailableMinute = (minuteSlots, fromMinute) => {
    const from = toInt(fromMinute);
    for (let i = minuteSlots.length - 1; i >= 0; i--) {
        if (toInt(minuteSlots[i]) <= from) return minuteSlots[i];
    }
    return minuteSlots[0];
};

export const deriveInitialTime = (currentTime, minuteSlots) => {
    if (!currentTime) {
        return { hour: DEFAULT_HOUR, minute: DEFAULT_MINUTE };
    }

    return {
        hour: currentTime.hour,
        minute: findLastAvailableMinute(minuteSlots, currentTime.minute),
    };
};

export const useTimeConstraints = (isToday, storedDepartureTime, minuteSlots) => {
    const currentTime = useMemo(() => (isToday ? TimeUtils.getCurrentTime() : null), [isToday]);

    // baselineTime acts as the default if empty, AND the strict minimum if isToday === true
    const baselineTime = useMemo(() => {
        return deriveInitialTime(currentTime, minuteSlots);
    }, [currentTime, minuteSlots]);

    const validTime = useMemo(() => {
        if (storedDepartureTime && storedDepartureTime.length === 4) {
            if (!isToday) return storedDepartureTime;

            // Today: ensure stored time isn't in the past
            const sH = toInt(storedDepartureTime.slice(0, 2));
            const sM = toInt(storedDepartureTime.slice(2, 4));
            const mH = toInt(baselineTime.hour);
            const mM = toInt(baselineTime.minute);

            if (sH > mH || (sH === mH && sM >= mM)) {
                return storedDepartureTime;
            }
        }
        // Fallback to baseline if nothing stored or if today's constraints were violated
        return `${baselineTime.hour}${baselineTime.minute}`;
    }, [storedDepartureTime, isToday, baselineTime]);

    // UI Constraints: Only restrict selection if it is "Today". Otherwise, allow from 00:00.
    const minimumHour = isToday ? baselineTime.hour : '00';

    const minimumMinute = useMemo(() => {
        if (!isToday) return '00';

        const selectedH = toInt(validTime.slice(0, 2));
        const minH = toInt(minimumHour);

        if (selectedH > minH) return '00';
        return baselineTime.minute;
    }, [isToday, validTime, minimumHour, baselineTime.minute]);

    return {
        hours: validTime.slice(0, 2),
        minutes: validTime.slice(2, 4),
        validTime,
        minimumHour,
        minimumMinute,
    };
};
